import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Inbox, Mail, MailCheck, Plus } from 'lucide-react';
import {
  type AutoReplyRequest,
  type FilterRule,
  type ImapAccount,
  connectImapAccount,
  createRule,
  listImapAccounts,
  listRules,
  setAutoReply,
} from '@/api/imapInbox';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';

export function IncomingEmailPage() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<ImapAccount[]>([]);
  const [selected, setSelected] = useState<ImapAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConnect, setShowConnect] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listImapAccounts()
      .then((rows) => {
        setAccounts(rows);
        setSelected((cur) => rows.find((a) => a.id === cur?.id) ?? rows[0] ?? null);
      })
      .catch(() => setAccounts([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-[var(--color-lop-text-primary)]">
            <Inbox className="h-6 w-6" />
            {t('incomingEmail.title')}
          </h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('incomingEmail.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => setShowConnect(true)} data-testid="imap-connect">
          <Plus className="mr-1 h-4 w-4" />
          {t('incomingEmail.connect')}
        </Button>
      </header>

      {loading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}

      {!loading && accounts.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-lg border bg-card p-10 text-center">
          <Mail className="h-10 w-10 opacity-40" />
          <p className="text-sm text-muted-foreground">{t('incomingEmail.empty')}</p>
          <Button size="sm" onClick={() => setShowConnect(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t('incomingEmail.connect')}
          </Button>
        </div>
      )}

      {!loading && accounts.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2">
            {accounts.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setSelected(a)}
                data-testid="imap-account"
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm ${
                  selected?.id === a.id
                    ? 'border-[var(--color-lop-accent)] bg-[var(--color-lop-accent)]/15 text-[var(--color-lop-text-primary)]'
                    : 'border-[var(--color-lop-border)] text-[var(--color-lop-text-secondary)] hover:bg-white/5'
                }`}
              >
                <Mail className="h-4 w-4" />
                {a.email}
                {!a.enabled && (
                  <span className="rounded-full bg-zinc-800 px-1.5 text-[10px] text-zinc-400">
                    {t('incomingEmail.disabled')}
                  </span>
                )}
              </button>
            ))}
          </div>

          {selected && (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <RulesPanel account={selected} />
              <AutoReplyPanel account={selected} />
            </div>
          )}
        </>
      )}

      {showConnect && (
        <ConnectModal
          onClose={() => setShowConnect(false)}
          onConnected={() => {
            setShowConnect(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function RulesPanel({ account }: { account: ImapAccount }) {
  const { t } = useTranslation();
  const [rules, setRules] = useState<FilterRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listRules(account.id)
      .then(setRules)
      .catch(() => setRules([]))
      .finally(() => setLoading(false));
  }, [account.id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section className="rounded-lg border bg-card">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-lop-text-primary)]">
          <Filter className="h-4 w-4" />
          {t('incomingEmail.rules.title')}
        </h2>
        <Button size="sm" variant="outline" onClick={() => setShowNew(true)} data-testid="imap-rule-new">
          <Plus className="mr-1 h-4 w-4" />
          {t('incomingEmail.rules.add')}
        </Button>
      </div>
      <div className="p-4">
        {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
        {!loading && rules.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('incomingEmail.rules.empty')}</p>
        )}
        <ul className="space-y-2">
          {rules.map((r) => (
            <li key={r.id} className="rounded border px-3 py-2" data-testid="imap-rule-row">
              <p className="text-sm font-medium text-[var(--color-lop-text-primary)]">{r.name}</p>
              <p className="mt-0.5 text-xs text-[var(--color-lop-text-muted)]">
                {(r.matchers ?? []).map((m) => `${m.field} ${m.op} "${m.value}"`).join(', ') || t('incomingEmail.rules.anyMessage')}
              </p>
              <p className="text-xs text-[var(--color-lop-text-secondary)]">
                → {(r.actions ?? []).map((a) => t(`incomingEmail.actionType.${a.type}`, a.type) + (a.value ? `: ${a.value}` : '')).join(', ')}
              </p>
            </li>
          ))}
        </ul>
      </div>
      {showNew && (
        <RuleModal
          accountId={account.id}
          sortOrder={rules.length * 10}
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            load();
          }}
        />
      )}
    </section>
  );
}

function RuleModal({
  accountId,
  sortOrder,
  onClose,
  onCreated,
}: {
  accountId: string;
  sortOrder: number;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [field, setField] = useState('from');
  const [op, setOp] = useState('contains');
  const [value, setValue] = useState('');
  const [actionType, setActionType] = useState('move_to');
  const [actionValue, setActionValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!name.trim() || !value.trim()) {
      setError(t('incomingEmail.rules.invalid'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createRule(accountId, {
        name: name.trim(),
        matchers: [{ field, op, value: value.trim() }],
        actions: [{ type: actionType, value: actionValue.trim() }],
        sortOrder,
      });
      onCreated();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to create rule');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('incomingEmail.rules.modalTitle')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('incomingEmail.rules.name')}
        </label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-3" data-testid="imap-rule-name" />
        <p className="mb-1 text-xs font-medium text-[var(--color-lop-text-secondary)]">
          {t('incomingEmail.rules.whenLabel')}
        </p>
        <div className="mb-3 grid grid-cols-3 gap-2">
          <Select value={field} onChange={(e) => setField(e.target.value)} data-testid="imap-rule-field">
            <option value="from">{t('incomingEmail.field.from')}</option>
            <option value="to">{t('incomingEmail.field.to')}</option>
            <option value="subject">{t('incomingEmail.field.subject')}</option>
            <option value="body">{t('incomingEmail.field.body')}</option>
          </Select>
          <Select value={op} onChange={(e) => setOp(e.target.value)}>
            <option value="contains">{t('incomingEmail.op.contains')}</option>
            <option value="equals">{t('incomingEmail.op.equals')}</option>
            <option value="starts_with">{t('incomingEmail.op.starts_with')}</option>
          </Select>
          <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={t('incomingEmail.rules.valuePlaceholder')} data-testid="imap-rule-value" />
        </div>
        <p className="mb-1 text-xs font-medium text-[var(--color-lop-text-secondary)]">
          {t('incomingEmail.rules.thenLabel')}
        </p>
        <div className="mb-4 grid grid-cols-2 gap-2">
          <Select value={actionType} onChange={(e) => setActionType(e.target.value)} data-testid="imap-rule-action">
            <option value="move_to">{t('incomingEmail.actionType.move_to')}</option>
            <option value="add_label">{t('incomingEmail.actionType.add_label')}</option>
            <option value="mark_read">{t('incomingEmail.actionType.mark_read')}</option>
            <option value="forward_to">{t('incomingEmail.actionType.forward_to')}</option>
          </Select>
          <Input
            value={actionValue}
            onChange={(e) => setActionValue(e.target.value)}
            placeholder={t('incomingEmail.rules.actionValuePlaceholder')}
            disabled={actionType === 'mark_read'}
          />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="imap-rule-submit">
            {t('common.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function AutoReplyPanel({ account }: { account: ImapAccount }) {
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      setError(t('incomingEmail.autoReply.invalid'));
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const req: AutoReplyRequest = {
        subject: subject.trim(),
        body: body.trim(),
        startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
        endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
      };
      await setAutoReply(account.id, req);
      setSaved(true);
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to save auto-reply');
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-lg border bg-card">
      <div className="border-b px-4 py-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-[var(--color-lop-text-primary)]">
          <MailCheck className="h-4 w-4" />
          {t('incomingEmail.autoReply.title')}
        </h2>
        <p className="text-xs text-[var(--color-lop-text-muted)]">{t('incomingEmail.autoReply.subtitle')}</p>
      </div>
      <form onSubmit={submit} className="space-y-3 p-4">
        {error && <p className="text-sm text-red-400">{error}</p>}
        {saved && <p className="text-sm text-emerald-400">{t('incomingEmail.autoReply.saved')}</p>}
        <div>
          <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
            {t('incomingEmail.autoReply.subject')}
          </label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="autoreply-subject" />
        </div>
        <div>
          <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
            {t('incomingEmail.autoReply.body')}
          </label>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} data-testid="autoreply-body" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('incomingEmail.autoReply.startsAt')}
            </label>
            <Input type="date" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('incomingEmail.autoReply.endsAt')}
            </label>
            <Input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end">
          <Button type="submit" size="sm" disabled={saving} data-testid="autoreply-submit">
            {t('incomingEmail.autoReply.save')}
          </Button>
        </div>
      </form>
    </section>
  );
}

function ConnectModal({ onClose, onConnected }: { onClose: () => void; onConnected: () => void }) {
  const { t } = useTranslation();
  const userId = useAuthStore((s) => s.user?.userId);
  const [provider, setProvider] = useState('gmail');
  const [email, setEmail] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!userId) {
      setError(t('incomingEmail.connectModal.noUser'));
      return;
    }
    if (!email.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await connectImapAccount({
        userId,
        provider,
        email: email.trim(),
        refreshToken: refreshToken.trim() || undefined,
      });
      onConnected();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to connect account');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('incomingEmail.connectModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('incomingEmail.connectModal.provider')}
        </label>
        <Select value={provider} onChange={(e) => setProvider(e.target.value)} className="mb-3" data-testid="imap-provider">
          <option value="gmail">Gmail</option>
          <option value="outlook">Outlook / Office 365</option>
          <option value="zoho">Zoho Mail</option>
          <option value="imap">{t('incomingEmail.connectModal.genericImap')}</option>
        </Select>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('incomingEmail.connectModal.email')}
        </label>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mb-3" data-testid="imap-email" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('incomingEmail.connectModal.refreshToken')}
        </label>
        <Input value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} className="mb-1" />
        <p className="mb-4 text-[10px] text-[var(--color-lop-text-muted)]">{t('incomingEmail.connectModal.tokenHint')}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="imap-connect-submit">
            {t('incomingEmail.connectModal.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
